/**
 * ProfileTileCard — tile-density profile card.
 *
 * Used in surfaces that render profile results inside the same
 * responsive grid as video tiles (`Row` `STYLES.S12345` `asGrid`).
 * Visually mirrors the Figma reference (white card, purple-30 border,
 * 4px radius, centered avatar + name) and extends it with location,
 * follower / following counts, an optional bio quote, and the same
 * Follow + Share action cluster used by `community-playlist-row-header__actions`.
 *
 * **No hover transform** — by intent. The surrounding `row-v2__item`
 * is the same element the video grid uses, but the card itself opts
 * out of `transform: scale(...)` styling (the carousel hover effects
 * only fire on `row-v2__item--visible` inside non-grid Rows anyway).
 *
 * Data fetch: `useProfileByUid({ uid, language, jwt })`. Visibility
 * gating mirrors `UserProfileCardV2` so private profiles show a
 * minimal anonymous placeholder and per-field visibility is honored.
 *
 * @shared AiGuideSearchPage (profiles tab)
 */

import React, {
  lazy, Suspense, useCallback, useState,
} from 'react'
import { compose } from 'recompose'
import { connect as connectStaticText } from '../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../StaticText/StaticTextContext'
import { usePostHogEvent } from '../../../PostHog/index'
import Check from '../../../Icon/icons/Check/index'
import ProfileAvatarRing from '../../Identity/shared/ProfileAvatarRing/index'
import ProfileMetaLine from '../../Identity/shared/ProfileMetaLine/index'
import ProfileShareButton from '../../Identity/shared/ProfileShareButton/index'
import FollowTag from '../FollowTag/index'
import useProfileByUid from '../../../../services/community/identity/profile/useProfileByUid'
import { isProfileVisible, isFieldVisible } from '../../../../services/community/index'
import type { StaticTextMap } from '../../../../services/community/types'

// Lazy-load to avoid SSR crash; ConnectionsModal is interaction-only.
const ConnectionsModal = lazy(() => import('../../Identity/shared/ConnectionsModal/index'))

export interface ProfileTileCardProps {
  /** Profile uid to display. */
  uid: string
  /** Active app language — required prop per Community CLAUDE.md rule #4. */
  language: string
  /** JWT token for the auth-gated profile query. Omit for anonymous viewers. */
  jwt?: string | null
  /** Static-text Map; connected automatically by the default export. */
  staticText: StaticTextMap
  /** Optional follow-state override (e.g. for optimistic updates from a
   *  parent list that already tracks per-row state). When omitted, the
   *  card reads `viewerContext.isFollowing` from the fetched profile. */
  isFollowing?: boolean
  /** Follow toggle handler. Suppressed when omitted (display-only mode)
   *  and when the viewer is the owner of the profile. */
  onFollowToggle?: (uid: string, currentlyFollowing: boolean) => void
  /** When true, the card represents the logged-in viewer themselves —
   *  the Follow + Share actions row is hidden because you can't follow
   *  yourself, and sharing your own profile from your own search is
   *  noise. The card still renders so the viewer sees they appear in
   *  search results. Parents that don't know the viewer's uid can
   *  leave this `false`; the card's own visibility gating still
   *  drops Follow when the fetched profile reports `isOwner`. */
  isViewer?: boolean
  /** Optional class for surface styling overrides. */
  className?: string
}

// Inner: lives inside the Outer's <StaticTextProvider>, so useTranslatedText
// resolves against the `profileTileCard` slice. No `staticText` prop — UI
// copy is resolved via hooks against the surrounding provider.
function ProfileTileCardInner ({
  uid,
  language,
  jwt = null,
  isFollowing,
  onFollowToggle,
  isViewer = false,
  className = '',
}: Omit<ProfileTileCardProps, 'staticText'>) {
  // ── Hooks (must run unconditionally) ───────────────────────────────
  const { profile, loading } = useProfileByUid({ uid, language, jwt })

  // Connections modal — clicking the count opens to followers / following
  // tab. Same UX as UserProfileCardV2.
  const [connectionsTab, setConnectionsTab] = useState<'followers' | 'following' | null>(null)
  const handleOpenFollowers = useCallback(() => setConnectionsTab('followers'), [])
  const handleOpenFollowing = useCallback(() => setConnectionsTab('following'), [])
  const handleCloseConnections = useCallback(() => setConnectionsTab(null), [])

  // Profile-share tracking — fires here (Redux is in scope on every
  // surface that renders this tile) so the ProfileShareButton can stay
  // presentational and Provider-free for unit testing.
  const trackProfileShared = usePostHogEvent('community_profile_shared')
  const targetDisplayName = profile?.displayName || undefined
  const handleProfileShared = useCallback((method: 'copy_link') => {
    trackProfileShared(method, targetDisplayName)
  }, [trackProfileShared, targetDisplayName])

  // Local optimistic follow state — flips immediately on click; FollowTag
  // exposes separate onClick / onUnfollow callbacks so we mirror the
  // ConnectionCard pattern.
  const [localFollowing, setLocalFollowing] = useState<boolean | null>(null)

  const resolvedIsFollowing = localFollowing
    ?? isFollowing
    ?? !!profile?.viewerContext?.isFollowing

  const handleFollow = useCallback(() => {
    if (!onFollowToggle) return
    setLocalFollowing(true)
    onFollowToggle(uid, false)
  }, [onFollowToggle, uid])

  const handleUnfollow = useCallback(() => {
    if (!onFollowToggle) return
    setLocalFollowing(false)
    onFollowToggle(uid, true)
  }, [onFollowToggle, uid])

  // ── Translated UI copy ──────────────────────────────────────────────
  // Resolved via the surrounding <StaticTextProvider> set up by the Outer
  // wrapper (defined below). All hook calls live before any conditional
  // return so call order is stable across renders.
  const anonymousLabel = useTranslatedText({ keys: ['anonymousLabel'] })
  const followsYouLabel = useTranslatedText({ keys: ['followsYouLabel'] })
  const memberSinceLabel = useTranslatedText({ keys: ['memberSinceYear'] })
  const locationLabel = useTranslatedText({ keys: ['locationLabel'] })
  const followerSingular = useTranslatedText({ keys: ['followerSingular'] })
  const followersLabel = useTranslatedText({ keys: ['followersLabel'] })
  const followingLabelText = useTranslatedText({ keys: ['followingLabel'] })

  // ── Pre-data render: empty shell so grid heights stay stable ───────
  if (loading && !profile) {
    return (
      <div
        className={`profile-tile-card profile-tile-card--loading${className ? ` ${className}` : ''}`}
        aria-hidden="true"
      />
    )
  }

  if (!profile) {
    return null
  }

  // ── Visibility gating (mirrors UserProfileCardV2) ──────────────────
  // Public-facing surface: downgrade 'owner' → 'member' so the profile
  // owner sees their own card the way other members would.
  const rawVisLevel = profile.viewerContext?.visibilityLevel ?? 'public'
  const visibilityLevel = rawVisLevel === 'owner' ? 'member' : rawVisLevel
  const profileIsVisible = isProfileVisible({
    profileVisibility: profile.profileVisibility ?? 'public',
    visibilityLevel,
  })

  const displayName = profile.displayName || 'Friend of Gaia'
  const avatarUrl = profile.avatar?.card || profile.avatar?.small || ''
  const profileHref = `/profile/${profile.uuid || uid}`

  // ── Anonymous / private profile variant ────────────────────────────
  if (!profileIsVisible) {
    return (
      <div className={`profile-tile-card profile-tile-card--anonymous${className ? ` ${className}` : ''}`}>
        <span className="profile-tile-card__avatar-link">
          <span className="profile-tile-card__avatar-placeholder">
            {anonymousLabel.charAt(0).toUpperCase()}
          </span>
        </span>
        <span className="profile-tile-card__name profile-tile-card__name--anonymous">
          {anonymousLabel}
        </span>
      </div>
    )
  }

  // ── Per-field visibility flags ─────────────────────────────────────
  const bio = profile.bio || ''
  const showBio = !!bio && isFieldVisible(profile.bioVisibility, visibilityLevel)
  const profileLocation = profile.location || ''
  const showLocation = !!profileLocation
    && isFieldVisible(profile.locationVisibility, visibilityLevel)
  const showMemberSince = isFieldVisible(profile.memberSinceVisibility, visibilityLevel)
  const memberYear = profile.createdAt ? new Date(profile.createdAt).getFullYear() : undefined

  // ── Follower / following counts ────────────────────────────────────
  const followerCount = profile.followerCount ?? 0
  const followingCount = profile.followingCount ?? 0
  const showCounts = followerCount > 0 || followingCount > 0
  const followerLabel = followerCount === 1 ? followerSingular : followersLabel

  // ── Follow / share guards ──────────────────────────────────────────
  const isOwner = !!profile.viewerContext?.isOwner
  const isFollowedBy = !!profile.viewerContext?.isFollowedBy
  // `isViewer` (parent-supplied uid match) and `isOwner` (server-supplied
  // viewerContext) both suppress self-follow. Either one being true
  // means this card is the viewer's own profile.
  const isSelf = isViewer || isOwner
  const showFollowTag = !!onFollowToggle && !isSelf
  const showActionsRow = !isSelf

  return (
    <div className={`profile-tile-card${className ? ` ${className}` : ''}`}>
      <a href={profileHref} className="profile-tile-card__avatar-link" aria-label={displayName}>
        {avatarUrl ? (
          <ProfileAvatarRing
            avatarUrl={avatarUrl}
            displayName={displayName}
            className="profile-tile-card__avatar"
          />
        ) : (
          <span className="profile-tile-card__avatar-placeholder">
            {(displayName.charAt(0) || '?').toUpperCase()}
          </span>
        )}
      </a>

      <a href={profileHref} className="profile-tile-card__name">
        {displayName}
      </a>

      <div className="profile-tile-card__body">
        {isFollowedBy ? (
          <span className="profile-tile-card__follows-you">
            <Check size="14px" color="currentColor" />
            {followsYouLabel}
          </span>
        ) : null}

        <ProfileMetaLine
          memberYear={memberYear}
          memberSinceLabel={memberSinceLabel}
          showMemberSince={showMemberSince && !!memberYear}
          location={profileLocation}
          locationLabel={locationLabel}
          showLocation={showLocation}
        />

        {showCounts ? (
          <span className="profile-tile-card__counts">
            <button
              type="button"
              className="profile-tile-card__count-link"
              onClick={handleOpenFollowers}
            >
              <strong>{followerCount}</strong>
              {' '}
              {followerLabel}
            </button>
            <span className="profile-tile-card__count-dot" aria-hidden="true" />
            <button
              type="button"
              className="profile-tile-card__count-link"
              onClick={handleOpenFollowing}
            >
              <strong>{followingCount}</strong>
              {' '}
              {followingLabelText}
            </button>
          </span>
        ) : null}

        {showBio ? (
          <p className="profile-tile-card__bio">{`"${bio}"`}</p>
        ) : null}
      </div>

      {/* Actions cluster mirrors `community-playlist-row-header__actions`:
          FollowTag + icon-only ProfileShareButton. The whole row is
          hidden when this card represents the viewer themselves —
          you can't follow yourself, and sharing your own profile from
          your own search results is noise. */}
      {showActionsRow ? (
        <div className="profile-tile-card__actions">
          {showFollowTag ? (
            <FollowTag
              isFollowing={resolvedIsFollowing}
              followerCount={followerCount}
              onClick={handleFollow}
              onUnfollow={handleUnfollow}
            />
          ) : null}
          <ProfileShareButton
            uid={profile.uuid || uid}
            iconOnly
            onShared={handleProfileShared}
          />
        </div>
      ) : null}

      {connectionsTab ? (
        <Suspense fallback={null}>
          <ConnectionsModal
            uid={uid}
            initialTab={connectionsTab}
            onClose={handleCloseConnections}
            jwt={jwt ?? undefined}
            displayName={displayName}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

// Outer wrapper: receives `staticText` from connectStaticText and provides
// it to the subtree so Inner's useTranslatedText hooks resolve against the
// `profileTileCard` slice. Mirrors the Oracle Outer/Inner pattern.
function ProfileTileCard ({ staticText, ...rest }: ProfileTileCardProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <ProfileTileCardInner {...rest} />
    </StaticTextProvider>
  )
}

export const COMPONENT_NAME = 'ProfileTileCard'

export { ProfileTileCard }

export default compose(
  connectStaticText({ storeKey: 'profileTileCard' }),
)(React.memo(ProfileTileCard))
